import { AxiosError } from 'axios';

export interface ValidationErrorDetail {
  index: number;
  message: string;
}

export interface ApiErrorResponse {
  message: string;
  error: string;
  details?: ValidationErrorDetail[];
}

export interface FormattedError {
  title: string;
  message: string;
  details?: string[];
}

/**
 * Formats API error responses into user-friendly error messages
 * Handles the specific error format from the bulk inventory creation API
 */
export const formatApiError = (error: { message: string; errorResponse?: AxiosError }): FormattedError => {
  // Default fallback error
  let formattedError: FormattedError = {
    title: 'Error',
    message: error.message || 'An unexpected error occurred. Please try again.'
  };

  try {
    // Try to extract detailed error information from the API response
    const apiResponse = error.errorResponse?.response?.data as ApiErrorResponse;

    if (apiResponse && typeof apiResponse === 'object') {
      // Check if this is a validation error with details
      if (apiResponse.error === 'inventory_validation_failed' && apiResponse.details) {
        formattedError = {
          title: 'Data Submission Errors',
          message:
            "Some inventory items failed validation or couldn't be added because they already exist in the system. Please review the details below:",
          details: formatValidationDetails(apiResponse.details)
        };
      } else if (apiResponse.message) {
        // Use the API's message but make it more user-friendly
        formattedError = {
          title: 'Error',
          message: makeMessageUserFriendly(apiResponse.message)
        };
      }
    }
  } catch (parseError) {
    // If we can't parse the error response, fall back to the original message
    console.warn('Failed to parse error response:', parseError);
  }

  return formattedError;
};

/**
 * Formats validation details into user-friendly messages
 */
const formatValidationDetails = (details: ValidationErrorDetail[]): string[] => {
  return details.map((detail, index) => {
    const itemNumber = detail.index + 1; // Make it 1-based for users
    const cleanedMessage = cleanValidationMessage(detail.message);
    return `Item ${itemNumber}: ${cleanedMessage}`;
  });
};

/**
 * Cleans up validation messages to be more user-friendly
 */
const cleanValidationMessage = (message: string): string => {
  // Split multiple validation errors by commas and clean each one
  const errors = message.split(',').map((error) => error.trim());

  const cleanedErrors = errors.map((error) => {
    // Common patterns to clean up
    let cleaned = error
      // Handle specific field name mappings
      .replace(/External A record/g, 'A record')
      .replace(/External can't be blank/g, 'External ID is required')
      .replace(/Term string at `\/term` does not match pattern: \^[^$]+\$/g, 'Term is required')
      .replace(/Status value at `\/status` is not one of: \[([^\]]+)\]/g, (match, options) => {
        const optionList = options.replace(/"/g, '').split(', ').join('; ');
        return `Status must be one of: ${optionList}`;
      })
      .replace(/Supplier can't be blank/g, 'Supplier is required')
      .replace(/Supplier value at `\/supplier_id` is not a string/g, 'Supplier ID must be text')
      // Remove field path references like "at `/term`"
      .replace(/at `[^`]+`/g, '')
      // Remove technical regex pattern details
      .replace(/does not match pattern: \^[^$]+\$/, 'has an invalid format')
      // Clean up "value at" constructions
      .replace(/value at `[^`]+`/g, 'field')
      // Make "can't be blank" more user-friendly
      .replace(/can't be blank/g, 'is required')
      // Clean up array notation in error messages (fallback for other fields)
      .replace(/is not one of: \[([^\]]+)\]/, (match, options) => {
        const optionList = options.replace(/"/g, '').split(', ').join('; ');
        return `must be one of: ${optionList}`;
      })
      // Clean up type validation messages
      .replace(/is not a string/g, 'must be text')
      .replace(/is not a number/g, 'must be a number')
      // Remove extra whitespace
      .replace(/\s+/g, ' ')
      .trim();

    // Capitalize first letter
    if (cleaned.length > 0) {
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }

    return cleaned;
  });

  return cleanedErrors.join('; ');
};

/**
 * Makes general error messages more user-friendly
 */
const makeMessageUserFriendly = (message: string): string => {
  // Common error message improvements
  const friendlyMessages: Record<string, string> = {
    'Validation failed for one or more items': 'Some inventory items contain invalid data and could not be created.',
    Unauthorized: 'You do not have permission to perform this action.',
    Forbidden: 'You do not have permission to access this resource.',
    'Not Found': 'The requested resource could not be found.',
    'Internal Server Error': 'A server error occurred. Please try again later.',
    'Bad Request': 'The request contained invalid data. Please check your input and try again.'
  };

  return friendlyMessages[message] || message;
};
